import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import '../styles/pages/company.css';

const BOOK_URL = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

const PILLARS = [
  {
    num: '01',
    icon: 'fa-bullseye',
    title: 'Mission',
    text: 'Transform complex business workflows into simple, intelligent automation that saves time and reduces human error.',
  },
  {
    num: '02',
    icon: 'fa-eye',
    title: 'Vision',
    text: 'A world where AI handles repetitive tasks, letting humans focus on creativity, strategy, and meaningful work.',
    note: true,
  },
  {
    num: '03',
    icon: 'fa-heart',
    title: 'Values',
    text: 'Privacy-first design, ethical AI, transparent partnerships, and measurable results that matter to your business.',
  },
];

const TIMELINE = [
  {
    year: '2019',
    label: 'The Problem',
    icon: 'fa-lightbulb',
    text: 'Witnessed countless businesses struggle with manual processes while AI remained in research labs.',
  },
  {
    year: '2020',
    label: 'The Solution',
    icon: 'fa-rocket',
    text: 'Founded Moneli to bridge the gap between AI research and practical business automation.',
  },
  {
    year: '2021-2024',
    label: 'The Results',
    icon: 'fa-trophy',
    text: 'Delivered 50+ projects, saved 1000+ hours weekly across healthcare, property management, and public sectors.',
  },
];

const VALUES = ['Privacy-first design', 'Ethical AI', 'Transparent partnerships', 'Measurable results'];

const PARTNERS = [
  {
    icon: 'fa-handshake',
    title: 'Industry Partnerships',
    text: 'Collaborations with Communitech, Ontario tech ecosystem, and academic institutions driving innovation.',
  },
  {
    icon: 'fa-graduation-cap',
    title: 'Mentorship & Education',
    text: 'Youth R&D engagement and mentorship programs fostering the next generation of AI innovators.',
  },
  {
    icon: 'fa-heart',
    title: 'Inclusive Design',
    text: 'Culturally safe communication and inclusive design principles in digital health and automation.',
  },
];

function About() {
  return (
    <>
      <PageHero
        variant="dark"
        id="about-hero"
        className="co-hero co-hero--about"
        eyebrow="Founded by AI researchers"
        title={<>We turn AI research into real <em>business</em> results</>}
        lead="Founded by two PhDs, we bridge the gap between advanced AI research and practical automation that saves you 20+ hours weekly."
        crumbs={[{ to: '/', label: 'Home' }, { label: 'About' }]}
        actions={<>
          <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
            <i className="fa-solid fa-calendar-check" aria-hidden="true" />
            Book a Demo
          </a>
          <Link to="/solutions" className="btn btn-outline-light btn-lg">
            View Solutions <i className="fa-solid fa-arrow-right" aria-hidden="true" />
          </Link>
        </>}
      >
        <div className="stats co-hero__stats" data-reveal-stagger>
          <div className="stat">
            <p className="stat__num"><span data-count="15">15</span><span className="stat__suffix">+</span></p>
            <p className="stat__label">Years experience</p>
          </div>
          <div className="stat">
            <p className="stat__num"><span data-count="50">50</span><span className="stat__suffix">+</span></p>
            <p className="stat__label">Projects delivered</p>
          </div>
          <div className="stat">
            <p className="stat__num"><span data-count="3">3</span></p>
            <p className="stat__label">Industries</p>
          </div>
        </div>
      </PageHero>

      {/* MISSION & VALUES */}
      <section className="section section--light" aria-labelledby="about-mission-title">
        <div className="container">
          <SectionHeader
            index="01"
            eyebrow="Mission & values"
            title={<>What <em>drives</em> us</>}
            lead="What drives us to build better automation solutions."
            id="about-mission-title"
          />
          <div className="grid-3" data-reveal-stagger>
            {PILLARS.map((p) => (
              <article key={p.title} className={`card card--feature card--num co-pillar${p.note ? ' card--note' : ''}`} data-num={p.num}>
                <span className={`icon-tile${p.note ? ' icon-tile--ink' : ''}`}>
                  <i className={`fa-solid ${p.icon}`} aria-hidden="true" />
                </span>
                <h3 className="card__title">{p.title}</h3>
                <p className="card__text">{p.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="section section--sand" aria-labelledby="about-story-title">
        <div className="container">
          <div className="feature-split co-story">
            <div>
              <SectionHeader
                align="stack"
                index="02"
                eyebrow="Since 2019"
                title={<>Our <em>story</em></>}
                lead="Born from frustration with AI hype that doesn't deliver real results."
                id="about-story-title"
              />
              <ol className="co-timeline" data-reveal-stagger="0.12">
                {TIMELINE.map((t) => (
                  <li key={t.year} className="co-timeline__item">
                    <span className="co-timeline__dot" aria-hidden="true">
                      <i className={`fa-solid ${t.icon}`} />
                    </span>
                    <div>
                      <p className="co-timeline__year">{t.year} <span aria-hidden="true">&mdash;</span> {t.label}</p>
                      <p className="co-timeline__text">{t.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <figure className="co-story__media" data-parallax-trigger>
              <div className="frame frame--4x5 frame--zoom" data-reveal-img>
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1600&auto=format&fit=crop"
                  alt="Moneli Automation team collaborating on AI automation project development and research"
                  loading="lazy"
                  data-parallax="0.08"
                />
              </div>
              <figcaption className="card card--flat co-story__caption" data-reveal data-delay="0.3">
                <span className="icon-tile icon-tile--note icon-tile--sm"><i className="fa-solid fa-flask" aria-hidden="true" /></span>
                <div>
                  <h3 className="co-story__caption-title">Research Meets Practice</h3>
                  <p className="card__text">We don't just build AI - we solve real business problems with practical, scalable solutions.</p>
                </div>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* VALUES RAIL */}
      <section className="section section--dark section--tight grain co-rail" aria-label="Our values">
        <div className="marquee marquee--words marquee--fade" data-marquee data-speed="55">
          <div className="marquee__track">
            {VALUES.map((v) => (
              <span className="marquee__item" key={v}>{v}<span className="marquee__sep" aria-hidden="true" /></span>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIPS */}
      <section className="section section--light" aria-labelledby="about-partners-title">
        <div className="container">
          <SectionHeader
            index="03"
            eyebrow="Community"
            title={<>Partnerships &amp; <em>community</em></>}
            lead="Building the future together."
            id="about-partners-title"
          />
          <div className="grid-3" data-reveal-stagger>
            {PARTNERS.map((p) => (
              <article key={p.title} className="card card--feature co-partner">
                <span className="icon-tile"><i className={`fa-solid ${p.icon}`} aria-hidden="true" /></span>
                <h3 className="card__title">{p.title}</h3>
                <p className="card__text">{p.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default About;
